import React, { useMemo, useState } from 'react';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { RainbowButton } from '../ui/rainbow-button';
import ContactComponent from '../Common/ContactComponent/ContactComponent';
import { ShineBorder } from '../ui/shine-border';
import './AVDQuestionnaire.css';

type Option = { label: string; value: number };
type Question = { id: number; title: string; options: Option[] };

const block1: Question[] = [
  {
    id: 1,
    title: 'Vos applications métiers (ERP, CRM) sont :',
    options: [
      { label: '100 % on-premises', value: 0 },
      { label: 'Hybrides (on-prem + cloud)', value: 1 },
      { label: 'Déjà cloud/SaaS', value: 2 },
    ],
  },
  {
    id: 2,
    title: 'Vos collaborateurs accèdent à leur poste via :',
    options: [
      { label: 'PC fixe en local', value: 0 },
      { label: 'VPN distant', value: 1 },
      { label: 'Accès cloud/virtualisation (VDI/AVD)', value: 2 },
    ],
  },
  {
    id: 3,
    title: 'Les mises à jour systèmes et applicatives sont :',
    options: [
      { label: 'Manuelles', value: 0 },
      { label: 'Automatisées partiellement', value: 1 },
      { label: 'Centralisées et automatiques', value: 2 },
    ],
  },
];

const block2: Question[] = [
  {
    id: 4,
    title: 'MFA (authentification multifacteur) est :',
    options: [
      { label: 'Inexistant', value: 0 },
      { label: 'Activé seulement pour certains', value: 1 },
      { label: 'Activé pour tous', value: 2 },
    ],
  },
  {
    id: 5,
    title: 'Comment sécurisez-vous l’accès distant ? ',
    options: [
      { label: 'VPN seul', value: 0 },
      { label: 'VPN + MFA', value: 1 },
      { label: 'Zero Trust (Azure AD / AVD)', value: 2 },
    ],
  },
  {
    id: 6,
    title: 'Sauvegardes testées (restauration réelle) :',
    options: [
      { label: 'Jamais', value: 0 },
      { label: '1 fois/an', value: 1 },
      { label: 'Tous les mois / trimestre', value: 2 },
    ],
  },
];

const block3: Question[] = [
  {
    id: 7,
    title: 'Temps moyen pour équiper un nouvel employé :',
    options: [
      { label: '> 1 semaine', value: 0 },
      { label: '2–5 jours', value: 1 },
      { label: '< 24h', value: 2 },
    ],
  },
  {
    id: 8,
    title: 'Vos équipes peuvent travailler :',
    options: [
      { label: 'Uniquement au bureau', value: 0 },
      { label: 'En mobilité limitée', value: 1 },
      { label: 'Depuis tout appareil, en sécurité', value: 2 },
    ],
  },
  {
    id: 9,
    title: 'Vos applications critiques (Office, ERP, CRM) sont :',
    options: [
      { label: 'Installées localement', value: 0 },
      { label: 'Mixtes (local + SaaS)', value: 1 },
      { label: 'Accessibles directement via Teams/Cloud', value: 2 },
    ],
  },
];

const allQuestions: Question[] = [...block1, ...block2, ...block3];

const Separator = () => (
  <div className="tw-my-10 tw-flex tw-items-center tw-justify-center">
    <div className="tw-h-px tw-w-3/4 tw-bg-gradient-to-r tw-from-transparent tw-via-cyan tw-to-transparent tw-opacity-70" />
  </div>
);

function SectionHeader({ label, title }: { label: string; title: string }) {
  return (
    <div className="tw-mb-6">
      <div className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/5 tw-px-3 tw-py-1 tw-ring-1 tw-ring-white/10">
        <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-cyan" />
        <span className="tw-text-xs tw-font-medium tw-text-white/80">{label}</span>
      </div>
      <h3 className="tw-mt-3 tw-text-2xl tw-font-semibold tw-bg-gradient-to-r tw-from-white tw-to-white/70 tw-bg-clip-text tw-text-transparent">
        {title}
      </h3>
    </div>
  );
}

function QuestionBlock({ questions, answers, onChange }: {
  questions: Question[];
  answers: Record<number, number | null>;
  onChange: (id: number, value: number) => void;
}) {
  return (
    <div className="tw-space-y-6">
      {questions.map((q) => (
        <div key={q.id} className="tw-rounded-2xl tw-bg-white/5 tw-p-5 tw-ring-1 tw-ring-white/10">
          <p className="tw-mb-4 tw-text-white/90 tw-font-medium">{q.title}</p>
          <RadioGroup
            value={answers[q.id] !== null && answers[q.id] !== undefined ? String(answers[q.id]) : ''}
            onValueChange={(v) => onChange(q.id, Number(v))}
            className="tw-grid tw-gap-3 md:tw-grid-cols-3"
          >
            {q.options.map((opt, idx) => {
              const isSelected = answers[q.id] === opt.value;
              return (
                <label
                  key={idx}
                  className={[
                    'tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-p-4 tw-transition-all',
                    'tw-border tw-border-white/10 hover:tw-border-white/20',
                    isSelected ? 'tw-bg-white/10 tw-shadow-[0_0_0_1px_rgba(255,255,255,0.2)_inset]' : 'tw-bg-white/5',
                  ].join(' ')}
                >
                  <RadioGroupItem value={String(opt.value)} />
                  <span className="tw-text-white/90">{opt.label}</span>
                </label>
              );
            })}
          </RadioGroup>
        </div>
      ))}
    </div>
  );
}

export default function AVDQuestionnaire() {
  const [answers, setAnswers] = useState<Record<number, number | null>>(
    () => Object.fromEntries(allQuestions.map((q) => [q.id, null]))
  );
  const [contactOpen, setContactOpen] = useState(false);
  const [stickyTop, setStickyTop] = useState<number>(96); // default ~6rem
  const FADE = 72; // constant fade height under the progress bar
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const formContentRef = React.useRef<HTMLDivElement>(null);

  const score = useMemo<number>(() => {
    const vals = Object.values(answers) as Array<number | null>;
    return vals.reduce<number>((acc, v) => acc + (v ?? 0), 0);
  }, [answers]);

  const answeredCount = useMemo<number>(
    () => Object.values(answers).filter((v) => v !== null).length,
    [answers]
  );

  React.useEffect(() => {
    const computeTop = () => {
      const navbar = (document.querySelector('nav.header-nav') ||
        document.querySelector('.navbar') ||
        document.querySelector('nav')) as HTMLElement | null;
      const navH = navbar?.offsetHeight ?? 60;
      setStickyTop(navH + 16);
    };
    computeTop();
    window.addEventListener('resize', computeTop);
    return () => window.removeEventListener('resize', computeTop);
  }, []);

  // No scroll tracking needed anymore: fade handled by sticky overlay below.

  const result = useMemo(() => {
    if (score <= 9) {
      return {
        title: 'SI à risque',
        desc: "Manque de modernité et forte vulnérabilité.",
        color: 'tw-from-red-500/80 tw-to-orange-500/80',
      };
    }
    if (score <= 18) {
      return {
        title: 'SI en transition',
        desc: 'Hybride, sécurisé partiellement, mais peu agile.',
        color: 'tw-from-amber-400/80 tw-to-cyan-500/80',
      };
    }
    return {
      title: 'SI moderne & agile',
      desc: 'Déjà dans une logique cloud/Zero Trust, AVD peut renforcer la productivité et l’optimisation des coûts.',
      color: 'tw-from-emerald-400/80 tw-to-cyan-500/80',
    };
  }, [score]);

  const handleChange = (id: number, value: number) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const progress = Math.round((answeredCount / allQuestions.length) * 100);

  return (
    <div className="tw-relative tw-z-10 tw-mx-auto tw-max-w-6xl tw-px-4 md:tw-px-6">
      <div className="tw-mx-auto tw-mb-8 tw-max-w-3xl tw-text-center">
        <p className="tw-text-sm tw-uppercase tw-tracking-wide tw-text-white/70">
          Un questionnaire rapide pour mesurer le niveau de modernité, sécurité et agilité de votre SI.
        </p>
        <h2 className="tw-mt-2 tw-text-4xl tw-font-semibold tw-bg-gradient-to-r tw-from-white tw-to-white/70 tw-bg-clip-text tw-text-transparent">
          Évaluez votre infrastructure en 2 minutes
        </h2>
        <p className="tw-mt-3 tw-text-white/80">Azure Virtual Desktop by IT Monkeys</p>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-10 lg:tw-grid-cols-3">
        <div className="tw-space-y-10 lg:tw-col-span-2">
          {/* Sticky progress bar at the top of the form */}
          <div ref={progressBarRef} className="tw-sticky tw-z-50" style={{ top: stickyTop }}>
            <div className="progress-track tw-h-2 tw-w-full tw-rounded-full tw-bg-white/10 tw-ring-1 tw-ring-white/10 tw-mb-2">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <p className="tw-text-right tw-text-xs tw-text-white/70 tw-mb-4">{answeredCount}/{allQuestions.length} complété(s)</p>
            {/* Constant fade overlay anchored below the sticky bar */}
            <div
              aria-hidden
              className="tw-absolute tw-left-0 tw-right-0 tw-top-full"
              style={{
                height: FADE,
                background: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,0))',
              }}
            />
          </div>
          <div 
            ref={formContentRef}
            style={{}}
          >
            <SectionHeader label="Bloc 1" title="Modernité IT" />
            <QuestionBlock questions={block1} answers={answers} onChange={handleChange} />

            <Separator />

            <SectionHeader label="Bloc 2" title="Sécurité & Résilience" />
            <QuestionBlock questions={block2} answers={answers} onChange={handleChange} />

            <Separator />

            <SectionHeader label="Bloc 3" title="Agilité & Expérience utilisateur" />
            <QuestionBlock questions={block3} answers={answers} onChange={handleChange} />
          </div>
        </div>

        <div className="tw-space-y-4">
          <div className="tw-sticky" style={{ top: stickyTop }}>
            <p className="tw-text-sm tw-font-medium tw-text-white/70">Résultats</p>
            <ShineBorder color={["#22d3ee", "#34d399", "#60a5fa"]} className="tw-w-full">
              <div className="tw-rounded-2xl tw-bg-white/10 tw-p-6 tw-backdrop-blur-sm">
                <div className="tw-mb-4 tw-flex tw-items-baseline tw-justify-between">
                  <span className="tw-text-white/70">Score</span>
                  <span className="tw-text-xs tw-text-white/60">max 27</span>
                </div>
                <div className="tw-flex tw-items-end tw-gap-3">
                  <span className="tw-text-5xl tw-font-semibold tw-text-white">{score}</span>
                  <span className="tw-text-white/60">/ 27</span>
                </div>
                <div className={`tw-mt-5 tw-rounded-xl tw-bg-gradient-to-r ${result.color} tw-px-4 tw-py-3 tw-text-black/90 tw-shadow-inner tw-ring-1 tw-ring-white/10`}>
                  <p className="tw-text-base tw-font-semibold">{result.title}</p>
                  <p className="tw-text-sm tw-text-black/80">{result.desc}</p>
                </div>

                <div className="tw-mt-5 tw-space-y-2">
                  <p className="tw-text-xs tw-text-white/70">Barème</p>
                  <ul className="tw-space-y-1 tw-text-sm tw-text-white/80">
                    <li>0–9 points → SI à risque</li>
                    <li>10–18 points → SI en transition</li>
                    <li>19–27 points → SI moderne & agile</li>
                  </ul>
                </div>

                <div className="tw-mt-6 tw-flex tw-flex-col tw-gap-3">
                  <p className="tw-text-sm tw-text-white/80">
                    🔒 Découvrez votre score complet avec recommandations IT Monkeys.
                  </p>
                  <RainbowButton className="tw-w-full tw-py-3" onClick={() => setContactOpen(true)}>
                    Obtenir mon rapport
                  </RainbowButton>
                </div>
              </div>
            </ShineBorder>

            <div className="tw-text-xs tw-text-white/60 tw-mt-2">
              Astuce: répondez à toutes les questions pour un score plus précis.
            </div>
          </div>
        </div>
      </div>

      {contactOpen && (
        <ContactComponent isOpen={true} onClose={() => setContactOpen(false)} />
      )}
    </div>
  );
}
